import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import BarChartOutlined from '@mui/icons-material/BarChartOutlined'
import DriveFileRenameOutlineOutlined from '@mui/icons-material/DriveFileRenameOutlineOutlined'

import { getActionbyIncident } from '../../redux/actions/actionActions'
import { getIncidentbyId } from '../../redux/incidents/incidentActions'
import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import { hasRole } from '../../utils/checkRole'
import { UserRole } from '../../types/keycloak'
import { formatTitle } from '../../utils/formatTitle'
import { writeEntityNav } from '../../utils/entityNav'
import UseAlert from '../../components/common/alert'
import {
  DetailAccordion,
  DetailButton,
  DetailInfoGrid,
  DetailInfoItem,
  DetailPageHead,
  RelatedActionsPanel,
} from '../../components/common/detail'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import '../../components/common/ListPageTable.css'

export default function IncidentDetail() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()
  const roles = useSelector((state: any) => state.auth.roles)

  const actionsList = useSelector(
    (state: RootState) => state.action.actionsbyIncident
  )
  const incident = useSelector((state: RootState) => state.incident.incident)
  const loading = useSelector((state: RootState) => state.action.loading)

  const [open, setOpen] = useState({ info: true, actions: true })
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  useEffect(() => {
    if (id) {
      dispatch(getIncidentbyId(id))
      dispatch(getActionbyIncident(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (!incident?._id) return
    writeEntityNav({
      incidentId: incident._id,
      incidentLabel: formatTitle(incident.IncidentName || ''),
      missionId: incident.MissionId || undefined,
      missionLabel: incident.MissionName
        ? formatTitle(incident.MissionName)
        : undefined,
    })
  }, [incident])

  const canUpdate =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.SSI_TEAM) ||
    hasRole(roles, UserRole.ADMIN)
  const canAnalyze =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.DSSI) ||
    hasRole(roles, UserRole.ADMIN)

  const title =
    formatTitle(incident?.IncidentName || '') || t('Incident')
  const rows = Array.isArray(actionsList) ? actionsList : []

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert((a) => ({ ...a, open: false }))}
      />

      <div className="detail-page">
        <DetailPageHead
          eyebrow={t('incidentDetailsPage.eyebrow', {
            defaultValue: 'Management · Incidents',
          })}
          title={title}
          subtitle={t('incidentDetailsPage.subtitle', {
            defaultValue:
              'Incident information and actions taken in response.',
          })}
          actions={
            <>
              {canAnalyze ? (
                <DetailButton
                  variant="ghost"
                  onClick={() => navigate(`/incident/view-analysis/${id}`)}
                >
                  <BarChartOutlined sx={{ fontSize: 16 }} />
                  {t('buttons.viewAnalysis')}
                </DetailButton>
              ) : null}
              {canUpdate ? (
                <DetailButton
                  onClick={() => navigate(`/incident/${incident?._id}`)}
                >
                  <DriveFileRenameOutlineOutlined sx={{ fontSize: 16 }} />
                  {t('Update Incident', { defaultValue: 'Update Incident' })}
                </DetailButton>
              ) : null}
            </>
          }
        />

        <DetailAccordion
          title={t('Information', { defaultValue: 'Information' })}
          open={open.info}
          onToggle={() => setOpen((p) => ({ ...p, info: !p.info }))}
        >
          <DetailInfoGrid>
            <DetailInfoItem label={t('Name', { defaultValue: 'Name' })} value={title} />
            <DetailInfoItem
              label={t('Description', { defaultValue: 'Description' })}
              value={
                <ListPageDescription
                  value={(incident as any)?.Description}
                />
              }
            />
            <DetailInfoItem
              label={t('Mission', { defaultValue: 'Mission' })}
              value={
                incident?.MissionName
                  ? formatTitle(incident.MissionName)
                  : '—'
              }
            />
          </DetailInfoGrid>
        </DetailAccordion>

        <RelatedActionsPanel
          title={t('Tasks', { defaultValue: 'Actions' })}
          listTitle={t('tasks_list', { defaultValue: 'Task list' })}
          emptyMessage={t('incidentDetailsPage.empty.actions', {
            defaultValue: 'No action is linked to this incident yet.',
          })}
          rows={rows}
          loading={loading}
          loadingDeps={[id]}
          roles={roles}
          open={open.actions}
          onToggle={() => setOpen((p) => ({ ...p, actions: !p.actions }))}
        />
      </div>
    </>
  )
}
